import {
  PortalUserCreateRequest,
  PortalUserResponse,
  PortalUserUpdateRequest,
} from '@b7hio/api-lib/src/ops-portal';
import { useTranslation } from 'react-i18next';
import { NextSeo } from 'next-seo';
import React from 'react';
import { PageHeader } from '../../components';
import {
  UserCreateDialog,
  UserDeleteDialog,
  UserList,
  UserUpdateDialog,
} from './components';

type Props = {
  // List State
  readonly data: readonly PortalUserResponse[];
  readonly isLoading: boolean;

  // Bank Data
  readonly partnerUid?: string;

  // View State
  readonly state: 'list' | 'create' | 'update' | 'delete' | 'duplicate';
  readonly target?: PortalUserResponse;
  readonly portal: 'banks' | 'ops';

  // Dialog Events
  readonly onClose: (event: object, reason: string) => void;
  readonly onRequestCreate: () => void;
  readonly onRequestUpdate: (user: PortalUserResponse) => void;
  readonly onRequestDelete: (user: PortalUserResponse) => void;
  readonly onRequestDuplicate: (user: PortalUserResponse) => void;

  // API Events
  readonly onCreate: (user: PortalUserCreateRequest) => Promise<void>;
  readonly onUpdate: (values: PortalUserUpdateRequest) => Promise<void>;
  readonly onDelete: (user: PortalUserResponse) => Promise<void>;
};

export const UserListPage = ({
  isLoading,
  data,
  state,
  target,
  portal,
  partnerUid,
  onRequestCreate,
  onRequestUpdate,
  onRequestDelete,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
}: Props): JSX.Element => {
  const { t } = useTranslation(['common', 'users']);

  return (
    <>
      <NextSeo title={t('users:pageTitle')} />
      <PageHeader
        title={t('users:pageTitle')}
        actionButton
        onClickButton={onRequestCreate}
        buttonText={t('users:createUser')}
      />
      <UserList
        isLoading={isLoading}
        data={data}
        onRequestUpdate={onRequestUpdate}
        onRequestDelete={onRequestDelete}
        portal={portal}
      />
      <UserCreateDialog
        portal={portal}
        partnerUid={partnerUid}
        open={state === 'create'}
        onSubmit={onCreate}
        onClose={onClose}
      />
      <UserUpdateDialog
        open={state === 'update'}
        user={target}
        onSubmit={onUpdate}
        onClose={onClose}
        portal={portal}
      />
      <UserDeleteDialog
        open={state === 'delete'}
        user={target}
        onSubmit={onDelete}
        onClose={onClose}
      />
    </>
  );
};
